import { useCallback, useEffect, useMemo, useState } from 'react';
import type {
  BankColumnMappingDto,
  BankFileFormat,
  BankFileRowDto,
  LockedPeriodImportCandidateDto,
} from '@signalvote/shared';
import { SignConvention } from '@signalvote/shared';
import { usePreviewBankMapping } from '@/hooks/queries/useBankIngestion';
import {
  ColumnMappingGrid,
  LockedPeriodSkipPanel,
  lockedIncludeHint,
} from '@/components/finance/import';
import type { ColumnTarget } from '@/components/finance/import';

interface BankColumnMappingPanelProps {
  /** The uploaded file's bytes (base64) — preview posts these (no batch yet). */
  fileBytesBase64: string;
  /** Original file name (optional; preview persists nothing). */
  fileName?: string;
  headers: string[];
  sampleRows: string[][];
  profileFormat: BankFileFormat;
  /** Called when the treasurer commits the mapping. */
  onConfirm: (input: {
    mapping: BankColumnMappingDto;
    dateFormat: string;
    signConvention: SignConvention;
    /**
     * The `rowIndex`es of already-filed (locked) rows the treasurer chose to
     * force-include. Omitted/empty ⇒ skip every locked row (the default).
     */
    includeLockedRowIndices?: number[];
    /**
     * The SHA-256 of the exact bytes this panel previewed, so the commit can bind
     * its force-includes to them (the sha-guard). Omitted when no preview ran.
     */
    previewFileSha256?: string;
  }) => void;
  onCancel: () => void;
  busy?: boolean;
}

// Bank target set for the universal ColumnMappingGrid. The target keys ARE the
// BankColumnMappingDto keys (sans the `Column` suffix), so the grid's
// target→index record maps straight onto the bank mapping.
const BANK_TARGETS: ColumnTarget[] = [
  { value: 'date', label: 'Date' },
  { value: 'description', label: 'Description' },
  { value: 'amount', label: 'Amount (signed)' },
  { value: 'debit', label: 'Debit / Withdrawal' },
  { value: 'credit', label: 'Credit / Deposit' },
  { value: 'reference', label: 'Reference / Check #' },
];

const SIGN_OPTIONS: { value: SignConvention; label: string; help: string }[] = [
  {
    value: SignConvention.SignedAmount,
    label: 'Single amount column (negative = debit)',
    help: 'Most banks. -40.00 means a withdrawal.',
  },
  {
    value: SignConvention.DebitCreditColumns,
    label: 'Separate debit / credit columns',
    help: 'River Bank & Trust, Citi business, etc.',
  },
  {
    value: SignConvention.ParensForNegatives,
    label: 'Parens for negatives (e.g. "(40.00)")',
    help: 'Some accounting exports.',
  },
];

const DATE_FORMAT_OPTIONS = [
  { value: 'MM/dd/yyyy', label: 'MM/dd/yyyy' },
  { value: 'yyyy-MM-dd', label: 'yyyy-MM-dd (ISO)' },
  { value: 'M/d/yyyy', label: 'M/d/yyyy' },
  { value: 'MM-dd-yyyy', label: 'MM-dd-yyyy' },
  { value: 'dd-MMM-yyyy', label: 'dd-MMM-yyyy' },
];

/**
 * PPR-004: column-mapping confirmation panel. Renders when the PPR-003
 * detector identifies a bank-shaped file but no <code>BankSourceProfile</code>
 * matches. Shows detected columns on the left, canonical-field dropdowns
 * on the right, plus a live preview of the first 5 rows under the current
 * selection.
 *
 * On confirm, the parent calls <code>useCommitBankIngest</code> with the
 * picked mapping; the backend creates a custom <code>BankSourceProfile</code>
 * and produces the BankTransaction rows.
 */
export function BankColumnMappingPanel({
  fileBytesBase64,
  fileName,
  headers,
  sampleRows,
  profileFormat,
  onConfirm,
  onCancel,
  busy,
}: BankColumnMappingPanelProps) {
  // Deterministic fuzzy pre-fill (the target→index record the grid seeds from).
  // A future LLM pre-fill would replace this with the SAME shape — the named,
  // deferred seam (no LLM in v1).
  const initialAssignments = useMemo(() => initialFieldAssignments(headers), [headers]);
  const [assignments, setAssignments] = useState<Record<string, number | null>>(initialAssignments);
  const [signConvention, setSignConvention] = useState<SignConvention>(SignConvention.SignedAmount);
  const [dateFormat, setDateFormat] = useState('MM/dd/yyyy');
  const [previewRows, setPreviewRows] = useState<BankFileRowDto[]>([]);
  const [previewError, setPreviewError] = useState<string | null>(null);
  // Rows dated in an already-filed (locked) period, computed over ALL parsed
  // rows by the bank preview. Skipped by default; `selectedLocked` holds the
  // rowIndexes the treasurer chose to force-include.
  const [lockedCandidates, setLockedCandidates] = useState<LockedPeriodImportCandidateDto[]>([]);
  const [selectedLocked, setSelectedLocked] = useState<number[]>([]);
  // The sha of the exact bytes the LAST successful preview parsed — threaded into
  // the confirm so the server can bind the force-includes to these bytes.
  const [previewSha, setPreviewSha] = useState<string | null>(null);

  const { mutateAsync: preview, isPending: previewing } = usePreviewBankMapping();

  const mapping = useMemo(() => assignmentsToMapping(assignments), [assignments]);
  const hasMinimumMapping = useMemo(
    () =>
      mapping.dateColumn != null
      && (mapping.amountColumn != null
        || (mapping.debitColumn != null && mapping.creditColumn != null)),
    [mapping],
  );

  // Re-preview when the mapping or sign convention changes.
  useEffect(() => {
    if (!hasMinimumMapping) {
      setPreviewRows([]);
      setLockedCandidates([]);
      setSelectedLocked([]);
      setPreviewSha(null);
      return;
    }
    let cancelled = false;
    preview({
      fileBytesBase64,
      fileName,
      mapping,
      dateFormat,
      signConvention,
      profileFormat,
    })
      .then((res) => {
        if (cancelled) return;
        setPreviewError(null);
        setPreviewRows(res.rows);
        setPreviewSha(res.previewFileSha256 ?? null);
        // Re-parsing under a new mapping recomputes which rows are locked, so
        // keep only force-includes that still point at a current candidate.
        const next = res.lockedPeriodCandidates ?? [];
        setLockedCandidates(next);
        setSelectedLocked((prev) => prev.filter((i) => next.some((c) => c.rowIndex === i)));
      })
      .catch((e) => {
        if (cancelled) return;
        setPreviewError(e instanceof Error ? e.message : 'Preview failed.');
        setPreviewRows([]);
        setLockedCandidates([]);
        setSelectedLocked([]);
        setPreviewSha(null);
      });
    return () => {
      cancelled = true;
    };
  }, [
    hasMinimumMapping,
    mapping,
    dateFormat,
    signConvention,
    profileFormat,
    fileBytesBase64,
    fileName,
    preview,
  ]);

  // Toggle one locked row's inclusion; include-all / skip-all across the set.
  const toggleLocked = useCallback((rowIndex: number) => {
    setSelectedLocked((prev) =>
      prev.includes(rowIndex) ? prev.filter((i) => i !== rowIndex) : [...prev, rowIndex],
    );
  }, []);

  const toggleAllLocked = useCallback(() => {
    const all = lockedCandidates.map((c) => c.rowIndex);
    setSelectedLocked((prev) => (all.every((i) => prev.includes(i)) ? [] : all));
  }, [lockedCandidates]);

  // Force-includes ordered by the candidate list (not click order) and filtered
  // to the current set, so the commit is deterministic and never stale.
  const orderedLockedIncludes = lockedCandidates
    .filter((c) => selectedLocked.includes(c.rowIndex))
    .map((c) => c.rowIndex);

  return (
    <div className="space-y-6 rounded-md border bg-card p-5">
      <header>
        <h3 className="text-base font-semibold">Tell us how to read this file</h3>
        <p className="mt-1 text-xs text-muted-foreground">
          We&apos;ll remember your choices for this bank so you don&apos;t have
          to pick again.
        </p>
      </header>

      <ColumnMappingGrid
        headers={headers}
        targets={BANK_TARGETS}
        assignments={assignments}
        onChange={setAssignments}
      />

      <section>
        <p className="mb-2 text-sm font-medium">How is the sign encoded?</p>
        <div className="space-y-1.5">
          {SIGN_OPTIONS.map((opt) => (
            <label key={opt.value} className="flex items-start gap-2 text-sm">
              <input
                type="radio"
                name="signConvention"
                value={opt.value}
                checked={signConvention === opt.value}
                onChange={() => setSignConvention(opt.value)}
                className="mt-1"
              />
              <span>
                <span className="font-medium">{opt.label}</span>
                <span className="block text-xs text-muted-foreground">{opt.help}</span>
              </span>
            </label>
          ))}
        </div>
      </section>

      <section>
        <p className="mb-2 text-sm font-medium">Date format:</p>
        <select
          aria-label="Date format"
          value={dateFormat}
          onChange={(e) => setDateFormat(e.target.value)}
          className="rounded-md border px-2 py-1 text-sm"
        >
          {DATE_FORMAT_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </section>

      <section aria-labelledby="preview-heading">
        <h4 id="preview-heading" className="mb-2 text-sm font-medium">
          Preview (first 5 rows):
        </h4>
        {!hasMinimumMapping && (
          <p className="text-xs text-muted-foreground">
            Pick at least Date and an amount column to see a preview.
          </p>
        )}
        {previewing && (
          <p className="text-xs text-muted-foreground">Re-parsing under the new mapping…</p>
        )}
        {previewError && (
          <p role="alert" className="text-xs text-red-700">
            {previewError}
          </p>
        )}
        {previewRows.length > 0 && (
          <table className="w-full text-xs">
            <thead className="border-b">
              <tr>
                <th className="px-2 py-1 text-left">Date</th>
                <th className="px-2 py-1 text-right">Amount</th>
                <th className="px-2 py-1 text-left">Description</th>
              </tr>
            </thead>
            <tbody>
              {previewRows.map((row, i) => (
                <tr key={i} className="border-b last:border-b-0">
                  <td className="px-2 py-1">{row.date}</td>
                  <td className="px-2 py-1 text-right tabular-nums">{row.amount.toFixed(2)}</td>
                  <td className="px-2 py-1">{row.description}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {sampleRows.length > 0 && previewRows.length === 0 && hasMinimumMapping && !previewing && !previewError && (
          <p className="text-xs text-muted-foreground">No rows produced — check the mapping.</p>
        )}
      </section>

      {/* Rows dated in an already-filed period: skipped by default, force-include
          any/all. The confirm payload carries the checked rowIndexes. Renders
          nothing when the bank preview flags none. */}
      <LockedPeriodSkipPanel
        candidates={lockedCandidates}
        selected={selectedLocked}
        onToggle={toggleLocked}
        onToggleAll={toggleAllLocked}
      />

      <footer className="flex items-center justify-end gap-3 border-t pt-4">
        <button
          type="button"
          onClick={onCancel}
          disabled={busy}
          className="rounded-md border px-3 py-1.5 text-sm hover:bg-muted disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() =>
            onConfirm({
              mapping,
              dateFormat,
              signConvention,
              ...(orderedLockedIncludes.length > 0
                ? { includeLockedRowIndices: orderedLockedIncludes }
                : {}),
              ...(previewSha ? { previewFileSha256: previewSha } : {}),
            })
          }
          disabled={busy || !hasMinimumMapping}
          className="rounded-md bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-700 disabled:opacity-50"
        >
          {busy
            ? 'Importing…'
            // The selection-reflecting hint appears only when the preview flags
            // already-filed rows to hold or include.
            : `Confirm and import${
                lockedCandidates.length > 0
                  ? lockedIncludeHint(selectedLocked.length)
                  : ''
              }`}
        </button>
      </footer>
    </div>
  );
}

/**
 * Deterministic fuzzy pre-fill: header text → a bank target key → column index
 * (first column wins per target). Returns the target→index record the universal
 * grid seeds from. The target keys match {@link BANK_TARGETS}.
 */
function initialFieldAssignments(headers: string[]): Record<string, number | null> {
  const out: Record<string, number | null> = {};
  const claim = (target: string, idx: number) => {
    if (out[target] == null) out[target] = idx;
  };
  headers.forEach((h, idx) => {
    const lower = h.toLowerCase().trim();
    if (/(date|posted|posting|transaction)/.test(lower) && !lower.includes('id')) claim('date', idx);
    else if (/(amount|amt)/.test(lower) && !lower.includes('balance')) claim('amount', idx);
    else if (/(debit|withdrawal)/.test(lower)) claim('debit', idx);
    else if (/(credit|deposit)/.test(lower)) claim('credit', idx);
    else if (/(description|memo|payee|name)/.test(lower)) claim('description', idx);
    else if (/(reference|check)/.test(lower)) claim('reference', idx);
  });
  return out;
}

/**
 * Map the grid's target→index record onto a {@link BankColumnMappingDto}. The
 * bank target keys are the dto keys minus the `Column` suffix.
 */
function assignmentsToMapping(assignments: Record<string, number | null>): BankColumnMappingDto {
  const at = (target: string) => assignments[target] ?? null;
  return {
    dateColumn: at('date'),
    amountColumn: at('amount'),
    descriptionColumn: at('description'),
    debitColumn: at('debit'),
    creditColumn: at('credit'),
    referenceColumn: at('reference'),
  };
}
